/* ==============================================================================
   Configurações do servidor
   ==============================================================================
   É a tela do DONO. Quatro seções, e cada uma responde a um pedido explícito:

     cargos       criar cargo, escolher o que ele pode, atribuir a membros
     canais       permissão por canal — inclusive "só comunicados, ninguém escreve"
     convites     gerar link, com cargo automático e validade
     integrações  gerar token de robô amarrado A ESTE servidor

   A decisão que atravessa a tela: mostrar o LIMITE, não escondê-lo
   ==============================================================================
   Um moderador não pode conceder permissão que ele não tem, nem editar cargo na
   sua altura. Em vez de esconder essas opções, a tela as mostra desabilitadas
   com o motivo — porque "por que não consigo editar este cargo?" é a dúvida mais
   comum de quem administra, e esconder não a responde.
   ============================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { useDialog } from '@concord/ui';
import { ApiError } from '@concord/core';
import { Permission, hasPermission, permissionsToNames } from '@concord/shared';

import type {
  Channel,
  ConcordApi,
  Guild,
  GuildToken,
  Invite,
  Role,
} from '../lib/concord.ts';

/**
 * Permissões oferecidas na interface, agrupadas.
 *
 * Lista curada e não `Object.entries(Permission)`: o bitfield tem 31 entradas,
 * e algumas (`MANAGE_INSTANCE`) não pertencem a uma guild. Uma lista gerada
 * mostraria opções que não fazem nada aqui, e o dono passaria tempo tentando
 * entender por que marcá-las não muda nada.
 */
const PERMISSION_GROUPS: Array<{
  title: string;
  items: Array<{ bit: bigint; label: string; hint: string }>;
}> = [
  {
    title: 'Texto',
    items: [
      { bit: Permission.VIEW_CHANNEL, label: 'Ver canais', hint: 'Sem isto, o canal nem aparece na lista.' },
      { bit: Permission.SEND_MESSAGES, label: 'Enviar mensagens', hint: 'Negue no @everyone para criar canal de comunicados.' },
      { bit: Permission.READ_MESSAGE_HISTORY, label: 'Ler histórico', hint: 'Sem isto, só vê o que chega depois de entrar.' },
      { bit: Permission.ATTACH_FILES, label: 'Anexar arquivos', hint: '' },
      { bit: Permission.ADD_REACTIONS, label: 'Reagir', hint: '' },
      { bit: Permission.MENTION_EVERYONE, label: 'Mencionar @everyone', hint: 'Notifica todo o servidor. Conceda com cuidado.' },
      { bit: Permission.MANAGE_MESSAGES, label: 'Apagar mensagens de outros', hint: 'Permissão de moderação.' },
    ],
  },
  {
    title: 'Voz e tela',
    items: [
      { bit: Permission.CONNECT, label: 'Entrar em canal de voz', hint: '' },
      { bit: Permission.SPEAK, label: 'Falar', hint: 'Sem isto, entra e ouve sem transmitir.' },
      { bit: Permission.VIDEO, label: 'Ligar câmera', hint: '' },
      { bit: Permission.SHARE_SCREEN, label: 'Compartilhar tela', hint: '' },
      {
        bit: Permission.SHARE_MULTI_SCREEN,
        label: 'Compartilhar VÁRIAS telas',
        hint: 'Cada tela extra multiplica o upload de quem compartilha pelo número de participantes. Duas telas para sete pessoas são quatorze fluxos de vídeo.',
      },
      { bit: Permission.PRIORITY_SPEAKER, label: 'Falante prioritário', hint: 'Abaixa o volume dos outros ao falar.' },
    ],
  },
  {
    title: 'Moderação',
    items: [
      { bit: Permission.MUTE_MEMBERS, label: 'Silenciar na voz', hint: 'Persiste entre entradas na sala.' },
      { bit: Permission.DEAFEN_MEMBERS, label: 'Ensurdecer na voz', hint: '' },
      { bit: Permission.MOVE_MEMBERS, label: 'Mover entre salas', hint: 'Só para salas em que o membro pode entrar.' },
      { bit: Permission.MODERATE_MEMBERS, label: 'Silenciar temporariamente', hint: 'Máximo de 28 dias. Acima disso, use banimento.' },
      { bit: Permission.KICK_MEMBERS, label: 'Expulsar', hint: 'O membro pode voltar com um convite.' },
      { bit: Permission.BAN_MEMBERS, label: 'Banir', hint: 'Impede de voltar.' },
      { bit: Permission.MANAGE_NICKNAMES, label: 'Alterar apelidos de outros', hint: '' },
    ],
  },
  {
    title: 'Servidor',
    items: [
      { bit: Permission.MANAGE_CHANNELS, label: 'Gerenciar canais', hint: 'Criar, renomear, apagar.' },
      { bit: Permission.MANAGE_ROLES, label: 'Gerenciar cargos', hint: 'Só cargos abaixo do próprio, e só permissões que já tem.' },
      { bit: Permission.MANAGE_GUILD, label: 'Gerenciar o servidor', hint: 'Nome, convites, configurações.' },
      { bit: Permission.MANAGE_WEBHOOKS, label: 'Gerenciar integrações', hint: 'Gerar e revogar tokens de robô.' },
      { bit: Permission.CREATE_INVITE, label: 'Criar convite', hint: '' },
      { bit: Permission.VIEW_AUDIT_LOG, label: 'Ver trilha de auditoria', hint: '' },
      {
        bit: Permission.ADMINISTRATOR,
        label: 'Administrador',
        hint: 'Concede TUDO e ignora restrição de canal. Equivale a copropriedade do servidor.',
      },
    ],
  },
];

const SCOPE_OPTIONS = [
  { value: 'messages:write', label: 'Publicar mensagens', hint: 'O escopo que um robô de notificação precisa.' },
  { value: 'messages:read', label: 'Ler mensagens', hint: '' },
  { value: 'channels:read', label: 'Listar canais', hint: 'Permite ao robô descobrir onde pode publicar.' },
  { value: 'members:read', label: 'Listar membros', hint: '' },
  { value: 'voice:observe', label: 'Observar estado de voz', hint: 'Quem está em chamada. NÃO dá acesso à mídia.' },
];

export interface GuildSettingsProps {
  api: ConcordApi;
  guild: Guild;
  channels: Channel[];
  /** Permissões do usuário nesta guild, já resolvidas. */
  permissions: bigint;
  onClose: () => void;
  onChanged: () => void;
}

type Tab = 'roles' | 'channels' | 'invites' | 'integrations';

export function GuildSettings({
  api,
  guild,
  channels,
  permissions,
  onClose,
  onChanged,
}: GuildSettingsProps) {
  const [tab, setTab] = useState<Tab>('roles');
  const [feedback, setFeedback] = useState<
    { kind: 'ok' | 'error'; message: string } | null
  >(null);

  const report = useCallback((kind: 'ok' | 'error', message: string) => {
    setFeedback({ kind, message });
  }, []);

  return (
    <div
      className="flex h-screen w-full"
      style={{ background: 'var(--color-surface-base)' }}
    >
      <nav
        className="flex w-56 shrink-0 flex-col gap-1 p-3"
        style={{ boxShadow: 'inset -1px 0 0 oklch(100% 0 0 / 0.055)' }}
      >
        <h1
          className="mb-0.5 truncate px-2 font-bold"
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          {guild.name}
        </h1>
        <p
          className="mb-2 px-2"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
        >
          {guild.isOwner ? 'você é o dono' : 'configurações'}
        </p>

        <NavItem active={tab === 'roles'} onClick={() => setTab('roles')}>
          Cargos e permissões
        </NavItem>
        <NavItem active={tab === 'channels'} onClick={() => setTab('channels')}>
          Canais
        </NavItem>
        <NavItem active={tab === 'invites'} onClick={() => setTab('invites')}>
          Convites
        </NavItem>
        <NavItem active={tab === 'integrations'} onClick={() => setTab('integrations')}>
          Integrações e robôs
        </NavItem>

        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1.5 text-left"
          style={{
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          ← Voltar ao servidor
        </button>
      </nav>

      <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
        {feedback !== null && (
          <p
            role="status"
            className="mb-4 px-3 py-2"
            style={{
              maxWidth: 640,
              borderRadius: 'var(--radius-md)',
              background:
                feedback.kind === 'ok'
                  ? 'var(--color-success-subtle)'
                  : 'var(--color-danger-subtle)',
              fontSize: 'var(--text-xs)',
              lineHeight: 'var(--leading-normal)',
              color:
                feedback.kind === 'ok'
                  ? 'var(--color-success)'
                  : 'var(--color-danger-on-subtle)',
            }}
          >
            {feedback.message}
          </p>
        )}

        {tab === 'roles' && (
          <RolesTab
            api={api}
            guild={guild}
            permissions={permissions}
            onReport={report}
            onChanged={onChanged}
          />
        )}
        {tab === 'channels' && (
          <ChannelsTab
            api={api}
            guild={guild}
            channels={channels}
            permissions={permissions}
            onReport={report}
          />
        )}
        {tab === 'invites' && (
          <InvitesTab api={api} guild={guild} channels={channels} onReport={report} />
        )}
        {tab === 'integrations' && (
          <IntegrationsTab
            api={api}
            guild={guild}
            channels={channels}
            permissions={permissions}
            onReport={report}
          />
        )}
      </div>
    </div>
  );
}

/* ==============================================================================
   Cargos
============================================================================== */

function RolesTab({
  api,
  guild,
  permissions,
  onReport,
  onChanged,
}: {
  api: ConcordApi;
  guild: Guild;
  permissions: bigint;
  onReport: (kind: 'ok' | 'error', message: string) => void;
  onChanged: () => void;
}) {
  const { ask } = useDialog();
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<bigint>(0n);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const list = await api.listRoles(guild.id);
      setRoles(list);
      setSelectedId((current) => current ?? list[0]?.id ?? null);
    } catch {
      onReport('error', 'Não foi possível carregar os cargos.');
    }
  }, [api, guild.id, onReport]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const selected = roles.find((r) => r.id === selectedId) ?? null;

  useEffect(() => {
    if (selected !== null) setDraft(BigInt(selected.permissions));
  }, [selected?.id, selected?.permissions]);

  const canManage = hasPermission(permissions, Permission.MANAGE_ROLES);
  const myTop = guild.isOwner
    ? Number.MAX_SAFE_INTEGER
    : Math.max(
        -1,
        ...roles
          .filter((r) => !r.isEveryone)
          .map((r) => r.position),
      );

  /**
   * Um cargo é editável se está ABAIXO do seu mais alto.
   *
   * O @everyone é a exceção: ele não é atribuível nem movível, mas as permissões
   * dele são exatamente o que se configura para "o que qualquer membro pode
   * fazer" — que é a primeira coisa que um dono ajusta.
   */
  const editable = (role: Role): { ok: boolean; reason: string | null } => {
    if (!canManage) {
      return { ok: false, reason: 'Você não tem a permissão "Gerenciar cargos".' };
    }
    if (guild.isOwner) return { ok: true, reason: null };
    if (role.isEveryone) return { ok: true, reason: null };
    if (role.position >= myTop) {
      return {
        ok: false,
        reason: 'Este cargo está na sua altura ou acima dela.',
      };
    }
    return { ok: true, reason: null };
  };

  const save = useCallback(async () => {
    if (selected === null) return;
    setSaving(true);
    try {
      await api.updateRole(guild.id, selected.id, { permissions: draft.toString() });
      onReport('ok', `Permissões de "${selected.name}" salvas.`);
      await reload();
      onChanged();
    } catch (error) {
      onReport(
        'error',
        error instanceof ApiError ? error.message : 'Não foi possível salvar.',
      );
    } finally {
      setSaving(false);
    }
  }, [api, guild.id, selected, draft, onReport, reload, onChanged]);

  const status = selected !== null ? editable(selected) : { ok: false, reason: null };
  const dirty = selected !== null && draft !== BigInt(selected.permissions);

  return (
    <div className="flex gap-6">
      {/* ---- Lista ---- */}
      <div className="w-56 shrink-0">
        <div className="mb-2 flex items-center justify-between">
          <h2
            className="font-semibold uppercase"
            style={{
              fontSize: 'var(--text-2xs)',
              letterSpacing: 'var(--tracking-wide)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            Cargos
          </h2>
          {canManage && (
            <button
              type="button"
              onClick={async () => {
                const name = await ask({
                  title: 'Criar cargo',
                  description:
                    'O cargo nasce ABAIXO do seu cargo mais alto — nascer acima ' +
                    'permitiria escalar privilégio em dois passos.',
                  label: 'Nome do cargo',
                  placeholder: 'Moderador',
                  maxLength: 100,
                  confirmLabel: 'Criar',
                  validate: (value) =>
                    value.length === 0 ? 'Informe um nome.' : null,
                });
                if (name === null) return;
                try {
                  await api.createRole(guild.id, { name });
                  onReport('ok', 'Cargo criado abaixo do seu cargo mais alto.');
                  await reload();
                } catch (error) {
                  onReport(
                    'error',
                    error instanceof ApiError ? error.message : 'Falha ao criar cargo.',
                  );
                }
              }}
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-accent-on-subtle)',
              }}
            >
              + novo
            </button>
          )}
        </div>

        <ul className="flex flex-col gap-0.5">
          {roles.map((role) => {
            const canEdit = editable(role);
            return (
              <li key={role.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(role.id)}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left"
                  style={{
                    borderRadius: 'var(--radius-md)',
                    background:
                      role.id === selectedId
                        ? 'var(--color-accent-subtle)'
                        : 'transparent',
                    fontSize: 'var(--text-xs)',
                    color:
                      role.id === selectedId
                        ? 'var(--color-accent-on-subtle)'
                        : 'var(--color-text-secondary)',
                    opacity: canEdit.ok ? 1 : 0.55,
                  }}
                  title={canEdit.reason ?? undefined}
                >
                  <span
                    aria-hidden="true"
                    className="block size-2 shrink-0 rounded-full"
                    style={{
                      background:
                        role.color !== null
                          ? `#${role.color.toString(16).padStart(6, '0')}`
                          : 'var(--color-text-disabled)',
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate">{role.name}</span>
                  <span
                    style={{
                      fontSize: 9,
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    {role.memberCount}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* ---- Editor ---- */}
      <div className="min-w-0 flex-1">
        {selected === null ? (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
            Selecione um cargo.
          </p>
        ) : (
          <>
            <header className="mb-4">
              <h2
                className="font-bold"
                style={{
                  fontSize: 'var(--text-lg)',
                  color: 'var(--color-text-primary)',
                  letterSpacing: 'var(--tracking-tight)',
                }}
              >
                {selected.name}
              </h2>
              <p
                className="mt-1"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {selected.isEveryone
                  ? 'É a base de permissão do servidor: vale para todo membro. Só as permissões são editáveis — ele não é renomeável nem movível.'
                  : `Posição ${selected.position} · ${selected.memberCount} ${
                      selected.memberCount === 1 ? 'membro' : 'membros'
                    }`}
              </p>
              {status.reason !== null && (
                <p
                  className="mt-2 px-2.5 py-1.5"
                  style={{
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-warning-subtle)',
                    fontSize: 'var(--text-2xs)',
                    color: 'var(--color-warning)',
                    maxWidth: 520,
                  }}
                >
                  {status.reason}
                </p>
              )}
            </header>

            {PERMISSION_GROUPS.map((group) => (
              <section key={group.title} className="mb-5">
                <h3
                  className="mb-2 font-semibold uppercase"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    letterSpacing: 'var(--tracking-wide)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {group.title}
                </h3>
                <div className="flex flex-col gap-2">
                  {group.items.map((item) => {
                    const enabled = (draft & item.bit) !== 0n;
                    /*
                     * Não se concede o que não se tem.
                     *
                     * O servidor recusa de qualquer forma; desabilitar aqui com
                     * o motivo evita que a pessoa marque a caixa, salve, e
                     * receba um erro que ela não relaciona àquela caixa
                     * específica.
                     */
                    const canGrant =
                      guild.isOwner ||
                      hasPermission(permissions, Permission.ADMINISTRATOR) ||
                      hasPermission(permissions, item.bit);

                    const blocked = !status.ok || !canGrant;

                    return (
                      <div key={item.label} style={{ opacity: blocked ? 0.55 : 1 }}>
                        <label className="flex cursor-pointer items-start gap-2.5">
                          <input
                            type="checkbox"
                            checked={enabled}
                            disabled={blocked}
                            onChange={(event) =>
                              setDraft((current) =>
                                event.target.checked
                                  ? current | item.bit
                                  : current & ~item.bit,
                              )
                            }
                            className="mt-0.5 shrink-0"
                            style={{ accentColor: 'var(--color-accent-solid)' }}
                          />
                          <span className="min-w-0">
                            <span
                              className="block"
                              style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              {item.label}
                            </span>
                            {(item.hint.length > 0 || !canGrant) && (
                              <span
                                className="block"
                                style={{
                                  fontSize: 'var(--text-2xs)',
                                  lineHeight: 'var(--leading-normal)',
                                  color: 'var(--color-text-tertiary)',
                                }}
                              >
                                {!canGrant
                                  ? 'Você não tem esta permissão, então não pode concedê-la.'
                                  : item.hint}
                              </span>
                            )}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {dirty && status.ok && (
              <div
                className="sticky bottom-0 flex items-center gap-3 px-3 py-2.5"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-surface-overlay)',
                  boxShadow:
                    'var(--glass-edge), 0 8px 24px oklch(0% 0 0 / 0.40), var(--glow-soft)',
                  maxWidth: 520,
                }}
              >
                <span
                  className="min-w-0 flex-1"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {permissionsToNames(draft).length} permissões marcadas
                </span>
                <button
                  type="button"
                  onClick={() => setDraft(BigInt(selected.permissions))}
                  style={{
                    fontSize: 'var(--text-2xs)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  Descartar
                </button>
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={saving}
                  className="px-3 py-1.5 font-medium"
                  style={{
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-accent-solid)',
                    color: 'var(--color-on-solid)',
                    fontSize: 'var(--text-2xs)',
                  }}
                >
                  {saving ? 'Salvando…' : 'Salvar'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ==============================================================================
   Canais — permissão por canal
============================================================================== */

function ChannelsTab({
  api,
  guild,
  channels,
  permissions,
  onReport,
}: {
  api: ConcordApi;
  guild: Guild;
  channels: Channel[];
  permissions: bigint;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    channels[0]?.id ?? null,
  );

  useEffect(() => {
    void api
      .listRoles(guild.id)
      .then(setRoles)
      .catch(() => onReport('error', 'Não foi possível carregar os cargos.'));
  }, [api, guild.id, onReport]);

  const canManage = hasPermission(permissions, Permission.MANAGE_ROLES);
  const everyone = roles.find((r) => r.isEveryone) ?? null;
  const selected = channels.find((c) => c.id === selectedChannelId) ?? null;

  /**
   * Transforma um canal em "só comunicados" com um clique.
   *
   * É duas chamadas: nega SEND_MESSAGES ao @everyone, e permite aos cargos
   * escolhidos. Fazer isso à mão exigiria entender overwrite, allow e deny — que
   * é o modelo correto e não é o que alguém quer aprender para publicar um aviso.
   */
  const makeAnnouncementOnly = useCallback(
    async (channelId: string, allowedRoleIds: string[]) => {
      if (everyone === null) return;
      try {
        await api.setChannelOverwrite(channelId, everyone.id, {
          targetType: 'role',
          allow: '0',
          deny: Permission.SEND_MESSAGES.toString(),
        });

        for (const roleId of allowedRoleIds) {
          await api.setChannelOverwrite(channelId, roleId, {
            targetType: 'role',
            allow: Permission.SEND_MESSAGES.toString(),
            deny: '0',
          });
        }

        onReport(
          'ok',
          allowedRoleIds.length > 0
            ? 'Canal configurado: ninguém escreve, exceto os cargos escolhidos.'
            : 'Canal configurado: ninguém escreve. Escolha ao menos um cargo para publicar.',
        );
      } catch (error) {
        onReport(
          'error',
          error instanceof ApiError ? error.message : 'Não foi possível configurar.',
        );
      }
    },
    [api, everyone, onReport],
  );

  return (
    <div className="flex gap-6">
      <div className="w-56 shrink-0">
        <h2
          className="mb-2 font-semibold uppercase"
          style={{
            fontSize: 'var(--text-2xs)',
            letterSpacing: 'var(--tracking-wide)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Canais
        </h2>
        <ul className="flex flex-col gap-0.5">
          {channels
            .filter((c) => c.type !== 'category')
            .map((channel) => (
              <li key={channel.id}>
                <button
                  type="button"
                  onClick={() => setSelectedChannelId(channel.id)}
                  className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left"
                  style={{
                    borderRadius: 'var(--radius-md)',
                    background:
                      channel.id === selectedChannelId
                        ? 'var(--color-accent-subtle)'
                        : 'transparent',
                    fontSize: 'var(--text-xs)',
                    color:
                      channel.id === selectedChannelId
                        ? 'var(--color-accent-on-subtle)'
                        : 'var(--color-text-secondary)',
                  }}
                >
                  <span style={{ opacity: 0.6 }}>
                    {channel.type === 'voice' ? '🔊' : channel.type === 'announcement' ? '📣' : '#'}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{channel.name}</span>
                </button>
              </li>
            ))}
        </ul>
      </div>

      <div className="min-w-0 flex-1" style={{ maxWidth: 620 }}>
        {selected === null ? (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
            Selecione um canal.
          </p>
        ) : (
          <>
            <h2
              className="mb-1 font-bold"
              style={{
                fontSize: 'var(--text-lg)',
                color: 'var(--color-text-primary)',
                letterSpacing: 'var(--tracking-tight)',
              }}
            >
              {selected.name}
            </h2>
            <p
              className="mb-4"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
            >
              {selected.type === 'voice'
                ? `Canal de voz${selected.maxParticipants !== null ? ` · limite ${selected.maxParticipants}` : ''}`
                : `Canal de texto${selected.rateLimitSeconds > 0 ? ` · modo lento ${selected.rateLimitSeconds}s` : ''}`}
              {selected.e2ee ? ' · criptografado ponta a ponta' : ''}
            </p>

            {!canManage && (
              <p
                className="mb-4 px-2.5 py-1.5"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-warning-subtle)',
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-warning)',
                }}
              >
                Editar permissão de canal exige "Gerenciar cargos".
              </p>
            )}

            {selected.type !== 'voice' && (
              <section className="mb-6">
                <h3
                  className="mb-1.5 font-semibold uppercase"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    letterSpacing: 'var(--tracking-wide)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  Canal de comunicados
                </h3>
                <p
                  className="mb-2.5"
                  style={{
                    fontSize: 'var(--text-xs)',
                    lineHeight: 'var(--leading-relaxed)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  Ninguém escreve, exceto os cargos escolhidos. Por baixo, isto nega
                  "Enviar mensagens" ao @everyone e permite aos cargos marcados — o
                  mesmo mecanismo que cria canal privado, negando "Ver canais".
                </p>

                <AnnouncementConfigurator
                  roles={roles.filter((r) => !r.isEveryone)}
                  disabled={!canManage || everyone === null}
                  onApply={(roleIds) =>
                    void makeAnnouncementOnly(selected.id, roleIds)
                  }
                />
              </section>
            )}

            <section>
              <h3
                className="mb-1.5 font-semibold uppercase"
                style={{
                  fontSize: 'var(--text-2xs)',
                  letterSpacing: 'var(--tracking-wide)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                Canal privado
              </h3>
              <p
                className="mb-2.5"
                style={{
                  fontSize: 'var(--text-xs)',
                  lineHeight: 'var(--leading-relaxed)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                Um canal sem "Ver canais" para o @everyone é OMITIDO da lista de quem
                não tem acesso — não aparece bloqueado. A existência do canal também
                é informação.
              </p>
              <button
                type="button"
                disabled={!canManage || everyone === null}
                onClick={async () => {
                  if (everyone === null) return;
                  try {
                    await api.setChannelOverwrite(selected.id, everyone.id, {
                      targetType: 'role',
                      allow: '0',
                      deny: (
                        Permission.VIEW_CHANNEL | Permission.CONNECT
                      ).toString(),
                    });
                    onReport(
                      'ok',
                      'Canal tornado privado. Conceda "Ver canais" aos cargos que devem enxergá-lo.',
                    );
                  } catch (error) {
                    onReport(
                      'error',
                      error instanceof ApiError
                        ? error.message
                        : 'Não foi possível configurar.',
                    );
                  }
                }}
                className="px-3 py-1.5 font-medium disabled:opacity-50"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-surface-hover)',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--text-2xs)',
                }}
              >
                Tornar privado
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function AnnouncementConfigurator({
  roles,
  disabled,
  onApply,
}: {
  roles: Role[];
  disabled: boolean;
  onApply: (roleIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  return (
    <div>
      <div className="mb-2 flex flex-col gap-1.5">
        {roles.length === 0 ? (
          <p style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
            Crie um cargo primeiro para escolher quem pode publicar.
          </p>
        ) : (
          roles.map((role) => (
            <label key={role.id} className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={selected.has(role.id)}
                disabled={disabled}
                onChange={(event) =>
                  setSelected((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(role.id);
                    else next.delete(role.id);
                    return next;
                  })
                }
                style={{ accentColor: 'var(--color-accent-solid)' }}
              />
              <span
                style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
              >
                {role.name}
              </span>
            </label>
          ))
        )}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={() => onApply([...selected])}
        className="px-3 py-1.5 font-medium disabled:opacity-50"
        style={{
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-accent-solid)',
          color: 'var(--color-on-solid)',
          fontSize: 'var(--text-2xs)',
        }}
      >
        Aplicar
      </button>
    </div>
  );
}

/* ==============================================================================
   Convites
============================================================================== */

function InvitesTab({
  api,
  guild,
  channels,
  onReport,
}: {
  api: ConcordApi;
  guild: Guild;
  channels: Channel[];
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [grantRoles, setGrantRoles] = useState<Set<string>>(new Set());
  const [maxUses, setMaxUses] = useState<string>('');
  const [expiry, setExpiry] = useState<string>('604800');
  const [channelId, setChannelId] = useState<string>('');

  const reload = useCallback(async () => {
    try {
      const [inviteList, roleList] = await Promise.all([
        api.listInvites(guild.id),
        api.listRoles(guild.id),
      ]);
      setInvites(inviteList);
      setRoles(roleList.filter((r) => !r.isEveryone));
    } catch (error) {
      onReport(
        'error',
        error instanceof ApiError ? error.message : 'Não foi possível carregar convites.',
      );
    }
  }, [api, guild.id, onReport]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div style={{ maxWidth: 680 }}>
      <h2
        className="mb-1 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Convites
      </h2>
      <p
        className="mb-5"
        style={{
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-relaxed)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Um convite pode conceder cargos automaticamente a quem entrar. Cada cargo
        passa pela mesma checagem de hierarquia da atribuição manual — senão o
        convite seria a porta lateral para dar qualquer cargo.
      </p>

      <section
        className="mb-6 px-4 py-3.5"
        style={{
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface-raised)',
        }}
      >
        <h3
          className="mb-3 font-semibold uppercase"
          style={{
            fontSize: 'var(--text-2xs)',
            letterSpacing: 'var(--tracking-wide)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Criar convite
        </h3>

        <div className="flex flex-col gap-3">
          <label className="block">
            <span
              className="mb-1 block"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
            >
              Validade
            </span>
            <select
              value={expiry}
              onChange={(event) => setExpiry(event.target.value)}
              style={inputStyle}
            >
              <option value="1800">30 minutos</option>
              <option value="86400">1 dia</option>
              <option value="604800">7 dias</option>
              <option value="2592000">30 dias</option>
              <option value="">Nunca expira</option>
            </select>
          </label>

          <label className="block">
            <span
              className="mb-1 block"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
            >
              Número máximo de usos
            </span>
            <input
              type="number"
              min={1}
              max={1000}
              value={maxUses}
              placeholder="ilimitado"
              onChange={(event) => setMaxUses(event.target.value)}
              style={inputStyle}
            />
          </label>

          <label className="block">
            <span
              className="mb-1 block"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
            >
              Canal de destino
            </span>
            <select
              value={channelId}
              onChange={(event) => setChannelId(event.target.value)}
              style={inputStyle}
            >
              <option value="">Nenhum específico</option>
              {channels
                .filter((c) => c.type === 'text' || c.type === 'announcement')
                .map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    #{channel.name}
                  </option>
                ))}
            </select>
          </label>

          {roles.length > 0 && (
            <div>
              <span
                className="mb-1.5 block"
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Conceder cargos ao entrar
              </span>
              <div className="flex flex-wrap gap-2">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className="flex cursor-pointer items-center gap-1.5 px-2 py-1"
                    style={{
                      borderRadius: 'var(--radius-full)',
                      background: grantRoles.has(role.id)
                        ? 'var(--color-accent-subtle)'
                        : 'var(--color-surface-hover)',
                      fontSize: 'var(--text-2xs)',
                      color: grantRoles.has(role.id)
                        ? 'var(--color-accent-on-subtle)'
                        : 'var(--color-text-secondary)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={grantRoles.has(role.id)}
                      onChange={(event) =>
                        setGrantRoles((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(role.id);
                          else next.delete(role.id);
                          return next;
                        })
                      }
                      className="sr-only"
                    />
                    {role.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={async () => {
              try {
                await api.createInvite(guild.id, {
                  channelId: channelId.length > 0 ? channelId : null,
                  maxUses: maxUses.length > 0 ? Number(maxUses) : null,
                  expiresInSeconds: expiry.length > 0 ? Number(expiry) : null,
                  grantRoles: [...grantRoles],
                });
                onReport('ok', 'Convite criado.');
                await reload();
              } catch (error) {
                onReport(
                  'error',
                  error instanceof ApiError
                    ? error.message
                    : 'Não foi possível criar o convite.',
                );
              }
            }}
            className="self-start px-3 py-1.5 font-medium"
            style={{
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-accent-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 'var(--text-xs)',
            }}
          >
            Gerar convite
          </button>
        </div>
      </section>

      <h3
        className="mb-2 font-semibold uppercase"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Convites ativos — {invites.length}
      </h3>

      {invites.length === 0 ? (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
          Nenhum convite ativo.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {invites.map((invite) => (
            <li
              key={invite.code}
              className="flex items-center gap-3 px-3 py-2"
              style={{
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-raised)',
              }}
            >
              <code
                className="min-w-0 flex-1 truncate"
                style={{
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {invite.url}
              </code>
              <span
                className="shrink-0"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {invite.uses}
                {invite.maxUses !== null ? `/${invite.maxUses}` : ''} usos
                {invite.expiresAt !== null
                  ? ` · expira ${new Date(invite.expiresAt).toLocaleDateString('pt-BR')}`
                  : ' · nunca expira'}
              </span>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(invite.url)
                    .then(() => onReport('ok', 'Link copiado.'))
                    .catch(() =>
                      onReport('error', 'Não foi possível copiar. Selecione e copie à mão.'),
                    );
                }}
                className="shrink-0 px-2 py-1"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-surface-hover)',
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                copiar
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.revokeInvite(invite.code);
                    onReport('ok', 'Convite revogado.');
                    await reload();
                  } catch {
                    onReport('error', 'Não foi possível revogar.');
                  }
                }}
                className="shrink-0 px-2 py-1"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-danger-subtle)',
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-danger-on-subtle)',
                }}
              >
                revogar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ==============================================================================
   Integrações
============================================================================== */

function IntegrationsTab({
  api,
  guild,
  channels,
  permissions,
  onReport,
}: {
  api: ConcordApi;
  guild: Guild;
  channels: Channel[];
  permissions: bigint;
  onReport: (kind: 'ok' | 'error', message: string) => void;
}) {
  const { confirm } = useDialog();
  const [tokens, setTokens] = useState<GuildToken[]>([]);
  const [commands, setCommands] = useState<
    Array<{ id: string; name: string; description: string }>
  >([]);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<Set<string>>(new Set(['messages:write']));
  const [defaultChannel, setDefaultChannel] = useState('');
  const [created, setCreated] = useState<{ token: string; warning: string } | null>(null);

  const canManage = hasPermission(permissions, Permission.MANAGE_WEBHOOKS);

  const reload = useCallback(async () => {
    try {
      const [tokenList, commandList] = await Promise.all([
        api.listGuildTokens(guild.id),
        api.listCommands(guild.id),
      ]);
      setTokens(tokenList);
      setCommands(commandList);
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        // Sem permissão é estado esperado, não erro. Um aviso vermelho aqui
        // faria parecer que algo quebrou.
        return;
      }
      onReport('error', 'Não foi possível carregar as integrações.');
    }
  }, [api, guild.id, onReport]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div style={{ maxWidth: 700 }}>
      <h2
        className="mb-1 font-bold"
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Integrações e robôs
      </h2>
      <p
        className="mb-5"
        style={{
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-relaxed)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        O token gerado aqui vale <strong>somente neste servidor</strong>. Ele é
        amarrado por coluna com chave estrangeira, não por texto num escopo — uma
        rota que esqueça o filtro devolve zero linhas em vez de acesso irrestrito.
        O teto de permissão é independente de quem criou: rebaixar o criador reduz
        o token na mesma hora.
      </p>

      {!canManage && (
        <p
          className="mb-5 px-3 py-2"
          style={{
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-warning-subtle)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-warning)',
          }}
        >
          Gerar e revogar tokens exige a permissão "Gerenciar integrações".
        </p>
      )}

      {created !== null && (
        <section
          className="mb-6 px-4 py-3.5"
          style={{
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-success-subtle)',
            boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
          }}
        >
          <h3
            className="mb-1.5 font-semibold"
            style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)' }}
          >
            Token criado
          </h3>
          <p
            className="mb-2"
            style={{
              fontSize: 'var(--text-2xs)',
              lineHeight: 'var(--leading-normal)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {created.warning}
          </p>
          <code
            className="block break-all px-2.5 py-2"
            style={{
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-sunken)',
              fontSize: 'var(--text-2xs)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-primary)',
            }}
          >
            {created.token}
          </code>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(created.token)
                  .then(() => onReport('ok', 'Token copiado.'))
                  .catch(() => onReport('error', 'Copie à mão.'));
              }}
              className="px-3 py-1.5 font-medium"
              style={{
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-accent-solid)',
                color: 'var(--color-on-solid)',
                fontSize: 'var(--text-2xs)',
              }}
            >
              Copiar
            </button>
            <button
              type="button"
              onClick={() => setCreated(null)}
              className="px-3 py-1.5"
              style={{
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              Já guardei
            </button>
          </div>
        </section>
      )}

      <section
        className="mb-6 px-4 py-3.5"
        style={{
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface-raised)',
          opacity: canManage ? 1 : 0.55,
        }}
      >
        <h3
          className="mb-3 font-semibold uppercase"
          style={{
            fontSize: 'var(--text-2xs)',
            letterSpacing: 'var(--tracking-wide)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Gerar token
        </h3>

        <div className="flex flex-col gap-3">
          <label className="block">
            <span
              className="mb-1 block"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
            >
              Nome (para você identificar depois)
            </span>
            <input
              type="text"
              value={name}
              disabled={!canManage}
              onChange={(event) => setName(event.target.value)}
              placeholder="Robô de alertas do monitoramento"
              style={inputStyle}
            />
          </label>

          <div>
            <span
              className="mb-1.5 block"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
            >
              O que o robô pode fazer
            </span>
            <div className="flex flex-col gap-1.5">
              {SCOPE_OPTIONS.map((scope) => (
                <label key={scope.value} className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    checked={scopes.has(scope.value)}
                    disabled={!canManage}
                    onChange={(event) =>
                      setScopes((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(scope.value);
                        else next.delete(scope.value);
                        return next;
                      })
                    }
                    className="mt-0.5"
                    style={{ accentColor: 'var(--color-accent-solid)' }}
                  />
                  <span>
                    <span
                      className="block"
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      {scope.label}
                    </span>
                    {scope.hint.length > 0 && (
                      <span
                        className="block"
                        style={{
                          fontSize: 'var(--text-2xs)',
                          color: 'var(--color-text-tertiary)',
                        }}
                      >
                        {scope.hint}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <label className="block">
            <span
              className="mb-1 block"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
            >
              Canal padrão de publicação
            </span>
            <select
              value={defaultChannel}
              disabled={!canManage}
              onChange={(event) => setDefaultChannel(event.target.value)}
              style={inputStyle}
            >
              <option value="">Nenhum — o robô informa o canal em cada chamada</option>
              {channels
                .filter((c) => c.type === 'text' || c.type === 'announcement')
                .map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    #{channel.name}
                  </option>
                ))}
            </select>
          </label>

          <button
            type="button"
            disabled={!canManage || name.trim().length === 0 || scopes.size === 0}
            onClick={async () => {
              try {
                const result = await api.createGuildToken(guild.id, {
                  name: name.trim(),
                  scopes: [...scopes],
                  defaultChannelId: defaultChannel.length > 0 ? defaultChannel : null,
                });
                setCreated({ token: result.token, warning: result.warning });
                setName('');
                await reload();
              } catch (error) {
                onReport(
                  'error',
                  error instanceof ApiError
                    ? error.message
                    : 'Não foi possível gerar o token.',
                );
              }
            }}
            className="self-start px-3 py-1.5 font-medium disabled:opacity-50"
            style={{
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-accent-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 'var(--text-xs)',
            }}
          >
            Gerar token
          </button>
        </div>
      </section>

      <h3
        className="mb-2 font-semibold uppercase"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Tokens ativos — {tokens.length}
      </h3>

      {tokens.length === 0 ? (
        <p
          className="mb-6"
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
        >
          Nenhum token gerado.
        </p>
      ) : (
        <ul className="mb-6 flex flex-col gap-1.5">
          {tokens.map((token) => (
            <li
              key={token.id}
              className="flex items-center gap-3 px-3 py-2"
              style={{
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-raised)',
              }}
            >
              <div className="min-w-0 flex-1">
                <p
                  className="truncate font-medium"
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {token.name}
                </p>
                <p
                  className="truncate"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {token.keyPrefix}… · {token.scopes.join(', ')}
                  {token.lastUsedAt !== null
                    ? ` · usado ${new Date(token.lastUsedAt).toLocaleDateString('pt-BR')}`
                    : ' · nunca usado'}
                </p>
              </div>
              <button
                type="button"
                disabled={!canManage}
                onClick={async () => {
                  const proceed = await confirm({
                    title: `Revogar "${token.name}"?`,
                    description:
                      'O robô para de funcionar imediatamente. O token não pode ' +
                      'ser recuperado — será preciso gerar outro e reconfigurar.',
                    confirmLabel: 'Revogar',
                    danger: true,
                  });
                  if (!proceed) return;
                  try {
                    await api.revokeGuildToken(guild.id, token.id);
                    onReport('ok', 'Token revogado.');
                    await reload();
                  } catch {
                    onReport('error', 'Não foi possível revogar.');
                  }
                }}
                className="shrink-0 px-2 py-1 disabled:opacity-50"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-danger-subtle)',
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-danger-on-subtle)',
                }}
              >
                revogar
              </button>
            </li>
          ))}
        </ul>
      )}

      <h3
        className="mb-2 font-semibold uppercase"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Comandos registrados — {commands.length}
      </h3>
      <p
        className="mb-2"
        style={{
          fontSize: 'var(--text-2xs)',
          lineHeight: 'var(--leading-normal)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        Um agente registra comandos com o token acima, por
        <code style={{ fontFamily: 'var(--font-mono)' }}>
          {' '}POST /api/v1/guilds/{guild.id}/commands
        </code>
        . Você pode remover daqui o que não deveria estar no servidor.
      </p>

      {commands.length === 0 ? (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
          Nenhum comando registrado.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {commands.map((command) => (
            <li
              key={command.id}
              className="flex items-center gap-3 px-3 py-2"
              style={{
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-raised)',
              }}
            >
              <div className="min-w-0 flex-1">
                <code
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  /{command.name}
                </code>
                <p
                  className="truncate"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {command.description}
                </p>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.deleteCommand(guild.id, command.id);
                    onReport('ok', 'Comando removido.');
                    await reload();
                  } catch {
                    onReport('error', 'Não foi possível remover.');
                  }
                }}
                className="shrink-0 px-2 py-1"
                style={{
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-danger-subtle)',
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-danger-on-subtle)',
                }}
              >
                remover
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------
   Estilos compartilhados
------------------------------------------------------------------------------ */

const inputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 400,
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-sunken)',
  boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text-primary)',
};

function NavItem({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-1.5 text-left transition-colors"
      style={{
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-xs)',
        fontWeight: active ? 600 : 400,
        background: active ? 'var(--color-accent-subtle)' : 'transparent',
        color: active ? 'var(--color-accent-on-subtle)' : 'var(--color-text-secondary)',
      }}
    >
      {children}
    </button>
  );
}
